import Vue from 'vue';
import Vuex from 'vuex';
import axios from 'axios';
import _ from 'lodash';
import dayjs from 'dayjs';

import createPersistedState from 'vuex-persistedstate';
import createMultiTabState from 'vuex-multi-tab-state'
import getBrowserFingerprint from '@/util/fingerPrint';
import config from "@/util/config";
import firebase from "@/plugins/Firebase";

const state = {
  user: null,
  estabelecimentoSelecionado: null,
  estabelecimentos: [],
  driver: null,
  ringtone: null,
  printer: null,
  print: null,
  printLayout: null,
  confirmation: null,
  loading: false,
  lastAccessNotification: null,
  enums: {
    VALOR_MINIMO_BOLETO: 20,
    TEMPO_CANCELAMENTO: 15,
  },
  token: null,
  tokenUpdatedAt: null,
  cardapio: [],
}

Vue.use(Vuex);

function getConfigUserNotFingerPrint(configDelivery){
  let configs;
  const deliveryKeys = Object.keys(configDelivery);

  deliveryKeys.forEach(key => {
    if (key !== '0') {
      configs = configDelivery[key];
    } else {
      configs = configDelivery[0];
    }
  });

  return configs;
}

function getDefaultConfig(){
  const defaultRingtone = config.optionsRingtones[0]
  const defaultDriver = config.optionsDrivers[0]
  const defaultPrinter = config.optionsPrinters[0]
  const defaultLayout = config.optionsLayout[0]
  const defaultConfirmation = config.optionsConfirmation[0]

  return {
    ringtone: defaultRingtone,
    printLayout: defaultLayout,
    print: defaultPrinter,
    driver: defaultDriver,
    printer: null,
    confirmation: defaultConfirmation,
  };
}

async function syncLocalData (token, dados) {
  try {
    const response = await axios.put(`${process.env.VUE_APP_API_URL}/configuracoes-delivery`, dados, {
      headers: {
        authorization: process.env.VUE_APP_AUTH_KEY,
        token: token,
      },
    });
  } catch (error) {
    let err = error.response && error.response.data ? error.response.data.error : 'Ocorreu um erro ao sincronizar seus dados!';
    console.error(error)
  }
}

const validateNewState = (new_state) => {
  if (
    new_state.token &&
    state.token &&
    new_state.token !== state.token &&
    new_state.tokenUpdatedAt &&
    state.tokenUpdatedAt &&
    new_state.tokenUpdatedAt < state.tokenUpdatedAt
  ) {
    return state
  }
  return new_state
}

const validateNewStateEstabelecimentos = (new_state) => {
  // remover propriedades não utilizadas para diminuir o tamanho do localStorage e evitar erro

  new_state.estabelecimentos = new_state.estabelecimentos
    .filter(e => e.ativo === 1 && (e.status_delivery === 1 || e.status_delivery === 4 || e.status_delivery === 0) && e.delivery !== null)
    .map(e =>{
      const omited =  _.omit(e, [
        'destaque',
        'comissao_entrega',
        'cupom_desconto',
        'descricao',
        'horario',
        'indice_delivery',
        'indice_destaque',
        'keywords',
        'oferta',
        'monitoramento_automatico',
        'pagamento_online',
        'plano1',
        'plano2',
        'plano3',
        'plano4',
        'versao',
      ]);

      const vendas = (e.vendas && e.vendas.length > 0 ? e.vendas : []).map(v => {
        return {
          status : v.status
        }
      });

      return {
        ...omited,
        vendas: vendas,
      }
    })
  return new_state
}

function setRemoteConfig(user, currentConfig) {

  if(!user) return null;

  const fingerprint = getBrowserFingerprint({ enableScreen: false });

  try{

    const configs = fingerprint &&
      user.configuracoes_delivery &&
      user.configuracoes_delivery[fingerprint] ?
      user.configuracoes_delivery[fingerprint] : user.configuracoes_delivery ? user.configuracoes_delivery["0"] : null;

    const configAplicavel = {
      ...(configs || getDefaultConfig()),
      ...(currentConfig || {})
    }

    const configuracoes_delivery = {
      ...(user.configuracoes_delivery || {}),
      ...{[fingerprint]: configAplicavel}
    }
    syncLocalData(user.token,  {
      id: user.id,
      fingerPrint: fingerprint,
      configuracoes_delivery
    })
    return configuracoes_delivery[fingerprint];

  }catch(e){
    console.error(e)
    return null;
  }
}

function updateRemoteConfig(state) {
  try{
    const currentConfig = {
      ringtone: state.ringtone,
      printLayout: state.printLayout,
      print: state.print,
      driver: state.driver,
      printer: state.printer,
      confirmation: state.confirmation,
    }
    if (window.getApp) {
      window.getApp.$emit("APP_UPDATE_CONFIG_DELIVERY", currentConfig);
    }
    setRemoteConfig(state.user, currentConfig);
  }catch(e){
    console.error(e)
  }
}

const store = new Vuex.Store({
  state: state,
  getters: {
    user (state) {
      return state.user;
    },
    estabelecimentoSelecionado (state) {
      return state.estabelecimentoSelecionado;
    },
    estabelecimentos (state) {
      return state.estabelecimentos;
    },
    currentConfig (state) {
      const fingerprint = getBrowserFingerprint({ enableScreen: false });
      const currentConfig = {
        fingerprint,
        ringtone: state.ringtone,
        printLayout: state.printLayout,
        print: state.print,
        driver: state.driver,
        printer: state.printer,
        confirmation: state.confirmation,
      }
      return currentConfig;
    },
    driver (state) {
      return state.driver;
    },
    ringtone (state) {
      return state.ringtone;
    },
    printer (state) {
      return state.printer;
    },
    print (state) {
      return state.print;
    },
    printLayout (state){
      return state.printLayout;
    },
    confirmation (state){
      return state.confirmation;
    },
    loading (state) {
      return state.loading;
    },
    lastAccessNotification (state) {
      return state.lastAccessNotification;
    },
    enums (state) {
      return state.enums;
    },
    token (state) {
      return state.token;
    },
    tokenUpdatedAt (state) {
      return state.tokenUpdatedAt;
    },
    cardapio (state) {
      return state.cardapio;
    },
  },
  actions: {
    setUser({ commit, state }, value) {
      if (value.configuracoes_delivery && typeof value.configuracoes_delivery == 'string') {
        value.configuracoes_delivery = JSON.parse(value.configuracoes_delivery);
      }
      if (value.updateConfigDelivery) {
        if(!state.user || !state.user.data_atualizacao_configuracoes_delivery ||
          !value.data_atualizacao_configuracoes_delivery ||
          new Date(state.user.data_atualizacao_configuracoes_delivery) < new Date(value.data_atualizacao_configuracoes_delivery)){

            const configAplicavel = setRemoteConfig(value);
            const configPrinter = Array.isArray(configAplicavel.printer) ? configAplicavel.printer : configAplicavel.printer ? [configAplicavel.printer] : [];

            if(configAplicavel && configAplicavel.driver) commit('setDriver', configAplicavel.driver);
            if(configAplicavel && configAplicavel.print) commit('setPrint', configAplicavel.print);
            if(configAplicavel && configAplicavel.printLayout) commit('setPrintLayout', configAplicavel.printLayout);
            if(configAplicavel && configAplicavel.ringtone) commit('setRingtone', configAplicavel.ringtone);
            if(configAplicavel && configAplicavel.printer) commit('setPrinter', configPrinter);
            if(configAplicavel && configAplicavel.confirmation) commit('setConfirmation', configAplicavel.confirmation);
        }
      }
      commit('setUser', value);
    },
    async setEstabelecimentoSelecionado({ commit, state }, value) {
      commit('setEstabelecimentoSelecionado', value);

      if (!value || !value.id) {
        return;
      }

      axios.get(`${process.env.VUE_APP_API_URL}/estabelecimento`, {
        headers: {
          authorization: process.env.VUE_APP_AUTH_KEY,
          token: state.token,
        },
        params: {
          id: value.id,
          delivery: true,
          ativo: 1,
          status_venda_delivery: [0,1,4],
          fields: 'delivery,vendas',
          columns: 'localidade,estado',
        },
      }).then(response => {
        const estabelecimentoAtualizado = response.data && response.data.length > 0 ? response.data.find(e => e.id === value.id) || value : value;

        if (state.estabelecimentos && state.estabelecimentos.length > 0) {
          const estabelecimentoNaLista = state.estabelecimentos.find(e => e.id === estabelecimentoAtualizado.id);
          if (estabelecimentoNaLista) {
            let estabelecimentos = _.cloneDeep(state.estabelecimentos);
            const index = _.findIndex(estabelecimentos, { id: estabelecimentoAtualizado.id });
            estabelecimentos = _.reject(estabelecimentos, { id: estabelecimentoAtualizado.id });
            commit('setEstabelecimentos', [...estabelecimentos.slice(0, index), estabelecimentoAtualizado, ...estabelecimentos.slice(index)]);
          }
        }

        if (state.estabelecimentoSelecionado && state.estabelecimentoSelecionado.id === estabelecimentoAtualizado.id) {
          if (state.estabelecimentoSelecionado.recessosVigentes && !estabelecimentoAtualizado.recessosVigentes) {
            estabelecimentoAtualizado.recessosVigentes = state.estabelecimentoSelecionado.recessosVigentes;
          }
          commit('setEstabelecimentoSelecionado', estabelecimentoAtualizado);
        }
      })
    },
    setEstabelecimentos({ commit }, value) {
      commit('setEstabelecimentos', value);
    },
    setDriver({ commit, state }, value) {
      commit('setDriver', value);

      updateRemoteConfig(state)
    },
    setRingtone({ commit, state }, value){
      commit('setRingtone', value);

      updateRemoteConfig(state)
    },
    setPrinter({ commit, state }, value){
      commit('setPrinter', value);

      updateRemoteConfig(state)
    },
    setPrint({ commit, state }, value){
      commit('setPrint', value);

      updateRemoteConfig(state)
    },
    setPrintLayout({ commit, state }, value){
      commit('setPrintLayout', value);

      updateRemoteConfig(state)
    },
    setConfirmation({ commit, state }, value){
      commit('setConfirmation', value);

      updateRemoteConfig(state)
    },
    setLoading({ commit }, value) {
      commit('setLoading', value);
    },
    setLastAccessNotification({ commit }, value) {
      const currentDate = dayjs().format();
      try {
        if (typeof value == 'string' && dayjs(value).isValid()) {
          commit('setLastAccessNotification', value);
        } else {
          commit('setLastAccessNotification', currentDate);
        }
      } catch (error) {
        commit('setLastAccessNotification', currentDate);
      }
    },
    setEnums({ commit }, value) {
      commit('setEnums', value);
    },
    setToken({ commit }, value) {
      commit('setToken', value);
    },
    setCardapio({ commit }, value) {
      commit('setCardapio', value);
    },
    resetStore({ commit }) {
      commit('setRingtone', null);
      commit('setDriver', null);
      commit('setPrinter', null);
      commit('setPrint', null);
      commit('setConfirmation', null);
      commit('setEstabelecimentos', []);
      commit('setEstabelecimentoSelecionado', null);
      commit('setLoading', false);
      commit('setLastAccessNotification', null);
      //commit('setPrintLayout', null);
      commit('setUser', null);
      commit('setToken', null);
    },
  },
  mutations: {
    setUser(state, value) {
      state.user = value;
    },
    setEstabelecimentoSelecionado(state, value) {
      if (JSON.stringify(value) !== JSON.stringify(state.estabelecimentoSelecionado)) {
        state.estabelecimentoSelecionado = value;
        if (firebase && firebase.setLocalidadeEstabelecimento && value) {
          firebase.setLocalidadeEstabelecimento({
            localidade: value.localidade,
            localidade_id: value.localidade_id,
            estabelecimento_id: value.id,
          });
        } else if (firebase && firebase.clearLocalidadeEstabelecimento && !value) {
          firebase.clearLocalidadeEstabelecimento();
        }
      }
    },
    setEstabelecimentos(state, value) {
      if (JSON.stringify(value) !== JSON.stringify(state.estabelecimentos)) {
        state.estabelecimentos = value;
      }
    },
    setDriver(state, value) {
      state.driver = value;
    },
    setRingtone(state, value) {
      state.ringtone = value;
    },
    setPrinter(state, value) {
      state.printer = value;
    },
    setPrint(state, value) {
      state.print = value;
    },
    setPrintLayout(state, value) {
      state.printLayout = value;
    },
    setConfirmation(state, value) {
      state.confirmation = value;
    },
    setLoading(state, value) {
      state.loading = value;
    },
    setLastAccessNotification(state, value) {
      state.lastAccessNotification = value;
    },
    setEnums(state, value) {
      state.enums = value;
    },
    setToken(state, value) {
      state.token = value;
      state.tokenUpdatedAt = value ? new Date().getTime() : null;
    },
    setCardapio(state, value) {
      state.cardapio = value;
    },
  },
  plugins: [
    createPersistedState(),
    createMultiTabState({
      key: 'vuex-multi-tab',
      statesPaths: [
        'token',
        'print',
        'printLayout',
        'confirmation',
        'ringtone',
        'driver',
        'user',
      ],
      onBeforeSave: validateNewState,
      onBeforeReplace: validateNewState,
    }),
    createMultiTabState({
      key: 'vuex-multi-tab-estabelecimentos',
      statesPaths: [
        'estabelecimentos',
      ],
      onBeforeSave: validateNewStateEstabelecimentos,
      onBeforeReplace: validateNewStateEstabelecimentos,
    }),
  ],
  strict: process.env.NODE_ENV !== 'production'
});

createPersistedState({
  key: 'vuex',
  paths: [],
  storage: window.localStorage,
})(store);

export default store;
